import {
    createContext,
    FC,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from "react";
import { ChatServiceNameEnum } from "@finbackoffice/fe-core";
import { useRuntimeConfig } from "../hooks";

interface IChatServiceContext {
    showWidget: () => void;
    hideWidget: () => void;
    syncVisibility: (visible: boolean) => void;
    setConversationFields: (
        username: string,
        userId: number | undefined,
        userEmail: string,
    ) => void;
    clearSession: () => void;
    visible: boolean;
}

export const ChatServiceContext = createContext<IChatServiceContext>(null as any);

export const ChatServiceProvider: FC<PropsWithChildren & { script: ReactNode }> = ({
    children,
    script,
}) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const [visible, setVisible] = useState(false);

    const hideWidget = useCallback(() => {
        try {
            switch (COMMON_SITE_CONFIGS.chatService.name) {
                case ChatServiceNameEnum.LiveChat:
                    if (window.LiveChatWidget) {
                        if (window.LiveChatWidget._h) {
                            window.LiveChatWidget.call("hide");
                        }
                        setVisible(false);
                    }
                    break;
                case ChatServiceNameEnum.Zendesk:
                    if (typeof window.zE === "function") {
                        try {
                            // Try new Web Widget (Messaging) first
                            window.zE("messenger", "hide");
                        } catch {
                            // Fallback to classic Web Widget
                            window.zE("webWidget", "hide");
                        }

                        setVisible(false);
                    }
                    break;
                case ChatServiceNameEnum.Tawk:
                    if (window.Tawk_API) {
                        window.Tawk_API.hideWidget();
                        setVisible(false);
                    }
                    break;
                default:
                    break;
            }
        } catch (error: unknown) {
            console.error(`Error hiding ${COMMON_SITE_CONFIGS.chatService.name} widget:`, error);
        }
    }, [COMMON_SITE_CONFIGS.chatService.name]);

    const showWidget = useCallback(() => {
        try {
            switch (COMMON_SITE_CONFIGS.chatService.name) {
                case ChatServiceNameEnum.LiveChat:
                    if (window.LiveChatWidget) {
                        if (window.LiveChatWidget._h) {
                            window.LiveChatWidget.call("minimize");
                        } else {
                            window.LiveChatWidget.init();
                        }
                        setVisible(true);
                    }
                    break;
                case ChatServiceNameEnum.Zendesk:
                    if (typeof window.zE === "function") {
                        try {
                            // Try new Web Widget (Messaging) first
                            window.zE("messenger", "show");
                        } catch {
                            // Fallback to classic Web Widget
                            window.zE("webWidget", "show");
                        }

                        setVisible(true);
                    }
                    break;
                case ChatServiceNameEnum.Tawk:
                    if (window.Tawk_API) {
                        window.Tawk_API.showWidget();
                        setVisible(true);
                    }
                    break;
                default:
                    break;
            }
        } catch (error: unknown) {
            console.error(`Error showing ${COMMON_SITE_CONFIGS.chatService.name} widget:`, error);
        }
    }, [COMMON_SITE_CONFIGS.chatService.name]);

    const syncVisibility = useCallback((v: boolean) => setVisible(v), []);

    const setConversationFields = useCallback(
        (username: string, userId: number | undefined, userEmail: string) => {
            try {
                switch (COMMON_SITE_CONFIGS.chatService.name) {
                    case ChatServiceNameEnum.LiveChat:
                        if (window.LiveChatWidget) {
                            window.LiveChatWidget.call("set_customer_name", username);
                        }
                        break;
                    case ChatServiceNameEnum.Zendesk:
                        if (typeof window.zE === "function") {
                            const fields: { id: string; value: string | number }[] = [];
                            const zendeskConfig = COMMON_SITE_CONFIGS.chatService.config;
                            if (zendeskConfig.userId && userId !== undefined) {
                                fields.push({ id: zendeskConfig.userId, value: userId });
                            }
                            if (zendeskConfig.username && username) {
                                fields.push({
                                    id: zendeskConfig.username,
                                    value: username,
                                });
                            }
                            if (zendeskConfig.userEmail && userEmail) {
                                fields.push({
                                    id: zendeskConfig.userEmail,
                                    value: userEmail,
                                });
                            }

                            if (fields.length > 0) {
                                window.zE("messenger:set", "conversationFields", fields);
                            }
                        }
                        break;
                    default:
                        break;
                }
            } catch (error: unknown) {
                console.error(
                    `Error setting conversation fields for ${COMMON_SITE_CONFIGS.chatService.name}:`,
                    error,
                );
            }
        },
        [COMMON_SITE_CONFIGS.chatService],
    );

    const clearSession = useCallback(() => {
        try {
            switch (COMMON_SITE_CONFIGS.chatService.name) {
                case ChatServiceNameEnum.Zendesk:
                    if (typeof window.zE === "function") {
                        window.zE("messenger", "logoutUser");
                    }
                    break;
                default:
                    break;
            }
        } catch (error: unknown) {
            console.error(
                `Error clearing session for ${COMMON_SITE_CONFIGS.chatService.name}:`,
                error,
            );
        }
    }, [COMMON_SITE_CONFIGS.chatService.name]);

    const value = useMemo(
        () => ({
            showWidget,
            hideWidget,
            syncVisibility,
            visible,
            setConversationFields,
            clearSession,
        }),
        [showWidget, hideWidget, syncVisibility, visible, setConversationFields, clearSession],
    );

    return (
        <ChatServiceContext.Provider value={value}>
            {COMMON_SITE_CONFIGS.chatService.enable && script}
            {children}
        </ChatServiceContext.Provider>
    );
};
